// События namespace `legal`: выход новой редакции документа.
import { z } from "zod";
import { IsoDateTimeSchema, SubjectSchema, UuidV7Schema } from "../_shared/primitives.js";
import {
	LegalDocumentKeySchema,
	LegalLocaleSchema,
	LegalVersionSchema,
	Sha256Schema,
} from "./schemas.js";

// ─── Socket.IO ──────────────────────────────────────────────────────────────

/**
 * Вышла новая редакция — открытая вкладка немедленно показывает баннер
 * «Points rules updated» (макет 3f), не дожидаясь перезагрузки.
 */
export const LegalNewVersionSocketEventSchema = z.object({
	channel: z.literal("user"),
	event: z.literal("legal:new-version"),
	payload: z.object({
		key: LegalDocumentKeySchema,
		version: LegalVersionSchema,
		materialChange: z.boolean(),
		effectiveFrom: IsoDateTimeSchema,
		blocking: z.boolean(),
	}),
});

export type LegalNewVersionSocketPayload = z.infer<
	typeof LegalNewVersionSocketEventSchema
>["payload"];

// ─── SET (RFC 8417) ─────────────────────────────────────────────────────────

/** URI типа события в реестре SET экосистемы. */
export const LEGAL_VERSION_PUBLISHED_SET_URI =
	"https://id.iec.exchange/secevent/event-type/legal-version-published";

/**
 * SET `legal-version-published` — вышла новая редакция документа, который
 * человек обязан принять (Д10).
 *
 * Зачем сервису это знать, если он согласий не собирает: пока редакция не
 * принята, `pendingAcceptance.blocking` у нас `true`, и сервис обязан отправить
 * человека на `auth.iec.exchange/consent?return_to=…`. Без события он узнает об
 * этом только на следующем чтении профиля — то есть, при живом кэше, никогда.
 *
 * Субъект — конкретный пользователь: событие адресуется тем, у кого с сервисом
 * ЖИВАЯ связь (инвариант №5), и означает «этому человеку нужно переподтвердить».
 */
export const LegalVersionPublishedSetPayloadSchema = z.object({
	subject: z.object({ format: z.literal("opaque"), id: SubjectSchema }),
	key: LegalDocumentKeySchema,
	version: LegalVersionSchema,
	sha256: Sha256Schema,
	materialChange: z.boolean(),
	requiresAcceptance: z.boolean(),
	effectiveFrom: IsoDateTimeSchema,
	/** true — до принятия сервис обязан редиректить на наш экран согласий. */
	blocking: z.boolean(),
	/** Прямая ссылка на текст редакции: сервису не нужно собирать её самому. */
	url: z.string().min(1),
});

export type LegalVersionPublishedSetPayload = z.infer<typeof LegalVersionPublishedSetPayloadSchema>;

// ─── Inngest ────────────────────────────────────────────────────────────────

/**
 * Редакция опубликована: пересчёт `needs-review` у всех, кто принимал прошлую,
 * рассылка уведомлений и (для существенного изменения) включение re-confirm.
 * Шаг долгий и батчевый — только durable-функция.
 *
 * В payload'е — только координаты редакции (`key` + `version`). Всё остальное
 * (`sha256`, `materialChange`, `requiresAcceptance`, `effectiveFrom`) функция
 * читает из БД: признак обязательности живёт в `legal_documents`, и второй его
 * источник — в теле события — разъехался бы с первым молча (В9).
 */
export const LegalVersionPublishedEventSchema = z.object({
	name: z.literal("legal/version.published"),
	data: z.object({
		documentId: UuidV7Schema,
		key: LegalDocumentKeySchema,
		version: LegalVersionSchema,
		locale: LegalLocaleSchema,
		materialChange: z.boolean(),
	}),
});
